import { useState } from "react";
import { BuilderPage, HeroBlockData, StoryBlockData, ApplicationsBlockData, CertificationsBlockData, CTABlockData, SpecsGridBlockData, GalleryBlockData } from "./types";

interface Props {
  pages: BuilderPage[];
  productName?: string;
  onClose: () => void;
}

type Device = "desktop" | "tablet" | "mobile";

// ── Responsive Block Renderers ────────────────────────────────────────────────

const HeroRender: React.FC<{ data: HeroBlockData; device: Device }> = ({ data, device }) => {
  const heights: Record<Device, number> = { desktop: 480, tablet: 360, mobile: 260 };
  const titleSizes: Record<Device, string> = { desktop: "text-4xl", tablet: "text-2xl", mobile: "text-xl" };
  const subtitleSizes: Record<Device, string> = { desktop: "text-lg", tablet: "text-base", mobile: "text-sm" };
  const paddings: Record<Device, string> = { desktop: "px-16 py-20", tablet: "px-10 py-14", mobile: "px-6 py-10" };

  return (
    <div className="relative w-full overflow-hidden" style={{ height: heights[device] }}>
      {data.backgroundImage ? (
        <img src={data.backgroundImage} alt="hero" className="absolute inset-0 w-full h-full object-cover object-top" />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-[#004A99] to-[#00AEEF]" />
      )}
      <div className="absolute inset-0" style={{ background: `rgba(0,0,0,${data.overlayOpacity / 100})` }} />
      <div className={`relative z-10 h-full flex flex-col justify-center gap-4 ${paddings[device]} ${
        data.textAlign === "center" ? "items-center text-center" :
        data.textAlign === "right" ? "items-end text-right" : "items-start text-left"
      }`}>
        {data.tagline && (
          <span className={`font-bold tracking-widest text-white/70 uppercase ${device === "mobile" ? "text-[10px]" : "text-xs"}`}>
            {data.tagline}
          </span>
        )}
        {data.title && (
          <h1 className={`font-bold text-white leading-tight ${titleSizes[device]}`}>{data.title}</h1>
        )}
        {data.subtitle && (
          <p className={`text-white/80 max-w-2xl ${subtitleSizes[device]}`}>{data.subtitle}</p>
        )}
        {data.ctaLabel && (
          <button className={`bg-white text-[#004A99] font-bold rounded-full cursor-pointer whitespace-nowrap mt-2 ${device === "mobile" ? "px-5 py-2 text-sm" : "px-8 py-3 text-base"}`}>
            {data.ctaLabel}
          </button>
        )}
      </div>
    </div>
  );
};

const StoryRender: React.FC<{ data: StoryBlockData; device: Device }> = ({ data, device }) => (
  <div className={`${device === "desktop" ? "px-16 py-12" : device === "tablet" ? "px-10 py-10" : "px-5 py-8"}`}>
    {data.items.map((item, i) => (
      <div
        key={item.id}
        className={`flex gap-8 items-center mb-12 ${
          device === "mobile" ? "flex-col" :
          item.imagePosition === "right" ? "flex-row-reverse" : "flex-row"
        }`}
      >
      {item.image && (
        <div className={`rounded-2xl overflow-hidden flex-shrink-0 ${
          device === "desktop" ? "w-80 h-56" : device === "tablet" ? "w-60 h-44" : "w-full h-48"
        }`}>
          <img src={item.image} alt="" className="w-full h-full object-cover object-top" />
        </div>
      )}
      <div className={`${!item.image ? "w-full max-w-3xl mx-auto text-center" : "flex-1"}`}>
        {item.accent && (
          <span className="text-xs font-bold tracking-wider text-[#004A99] uppercase bg-[#EDF2FB] px-3 py-1 rounded-full">
            {item.accent}
          </span>
        )}
        {item.title && (
          <h3 className={`font-bold text-gray-900 mt-3 ${device === "desktop" ? "text-2xl" : device === "tablet" ? "text-xl" : "text-lg"}`}>
            {item.title}
          </h3>
        )}
        <div
          className={`text-gray-600 mt-3 leading-relaxed ${device === "mobile" ? "text-sm" : "text-base"}`}
          dangerouslySetInnerHTML={{ __html: item.content }}
        />
      </div>
      </div>
    ))}
  </div>
);

const ApplicationsRender: React.FC<{ data: ApplicationsBlockData; device: Device }> = ({ data, device }) => {
  const cols: Record<Device, string> = { desktop: "grid-cols-3", tablet: "grid-cols-2", mobile: "grid-cols-1" };
  return (
    <div className={`${device === "desktop" ? "px-16 py-12" : device === "tablet" ? "px-10 py-10" : "px-5 py-8"}`}>
      <div className="text-center mb-8">
        <h2 className={`font-bold text-gray-900 ${device === "desktop" ? "text-3xl" : "text-2xl"}`}>{data.title}</h2>
        {data.subtitle && <p className="text-gray-500 mt-2">{data.subtitle}</p>}
      </div>
      <div className={`grid gap-6 ${cols[device]}`}>
        {data.items.map((item) => (
          <div key={item.id} className="rounded-2xl overflow-hidden border border-gray-100">
            {item.image_url && (
              <div className="h-48 overflow-hidden">
                <img src={item.image_url} alt={item.title} className="w-full h-full object-cover object-top" />
              </div>
            )}
            <div className="p-5">
              {item.title && <h4 className="font-bold text-gray-800 text-lg">{item.title}</h4>}
              {item.description && (
                <div className="text-gray-500 text-sm mt-2" dangerouslySetInnerHTML={{ __html: item.description }} />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const CertificationsRender: React.FC<{ data: CertificationsBlockData; device: Device }> = ({ data, device }) => {
  const cols: Record<Device, string> = { desktop: "grid-cols-4", tablet: "grid-cols-3", mobile: "grid-cols-2" };
  return (
    <div className={`bg-gray-50 ${device === "desktop" ? "px-16 py-12" : device === "tablet" ? "px-10 py-10" : "px-5 py-8"}`}>
      <h2 className={`font-bold text-gray-900 text-center mb-8 ${device === "desktop" ? "text-3xl" : "text-2xl"}`}>{data.title}</h2>
      <div className={`grid gap-4 ${cols[device]}`}>
        {data.items.map((item) => (
          <div key={item.id} className="bg-white rounded-2xl p-5 text-center border border-gray-100">
            {item.image_url && (
              <img src={item.image_url} alt={item.title} className="w-16 h-16 object-contain mx-auto mb-3" />
            )}
            <p className="font-bold text-gray-800 text-sm">{item.title}</p>
            {item.type && <span className="text-xs text-gray-400 capitalize">{item.type}</span>}
          </div>
        ))}
      </div>
    </div>
  );
};

const CTARender: React.FC<{ data: CTABlockData; device: Device }> = ({ data, device }) => (
  <div
    className={`text-center ${device === "desktop" ? "px-16 py-16" : device === "tablet" ? "px-10 py-12" : "px-6 py-10"}`}
    style={{ background: data.backgroundColor, color: data.textColor }}
  >
    <h2 className={`font-bold ${device === "desktop" ? "text-3xl" : "text-2xl"}`}>{data.title}</h2>
    {data.subtitle && <p className="opacity-80 mt-2 text-lg">{data.subtitle}</p>}
    {data.showForm && (
      <div className={`mt-8 mx-auto grid gap-4 ${device === "mobile" ? "grid-cols-1 max-w-sm" : "grid-cols-2 max-w-lg"}`}>
        {data.formFields.map((f, i) => (
          <div
            key={i}
            className={`bg-white/20 rounded-xl px-4 py-3 text-left ${f.type === "textarea" ? "col-span-full" : ""}`}
          >
            <label className="text-xs opacity-70 block mb-1">{f.label}{f.required ? " *" : ""}</label>
            {f.type === "textarea" ? (
              <div className="h-16 bg-white/10 rounded" />
            ) : (
              <div className="h-6 bg-white/10 rounded" />
            )}
          </div>
        ))}
      </div>
    )}
    <button
      className={`mt-6 rounded-full font-bold cursor-pointer whitespace-nowrap ${device === "mobile" ? "px-6 py-2.5 text-sm" : "px-10 py-3 text-base"}`}
      style={{ background: data.buttonColor, color: "#fff" }}
    >
      {data.buttonLabel}
    </button>
  </div>
);

const SpecsRender: React.FC<{ data: SpecsGridBlockData; device: Device }> = ({ data, device }) => {
  const colMap: Record<Device, string> = {
    desktop: data.columns === 2 ? "grid-cols-2" : data.columns === 4 ? "grid-cols-4" : "grid-cols-3",
    tablet: data.columns === 4 ? "grid-cols-3" : data.columns === 3 ? "grid-cols-3" : "grid-cols-2",
    mobile: "grid-cols-2",
  };
  return (
    <div className={`${device === "desktop" ? "px-16 py-12" : device === "tablet" ? "px-10 py-10" : "px-5 py-8"}`}>
      <h2 className={`font-bold text-gray-900 mb-6 ${device === "desktop" ? "text-3xl" : "text-2xl"}`}>{data.title}</h2>
      <div className={`grid gap-4 ${colMap[device]}`}>
        {data.items.map((item, i) => (
          <div key={i} className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
            <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider">{item.label}</p>
            <p className={`font-bold text-gray-900 mt-1 ${device === "mobile" ? "text-base" : "text-lg"}`}>{item.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const GalleryRender: React.FC<{ data: GalleryBlockData; device: Device }> = ({ data, device }) => {
  const colMap: Record<Device, string> = {
    desktop: data.layout === "2col" ? "grid-cols-2" : "grid-cols-3",
    tablet: "grid-cols-2",
    mobile: "grid-cols-1",
  };
  return (
    <div className={`${device === "desktop" ? "px-16 py-12" : device === "tablet" ? "px-10 py-10" : "px-5 py-8"}`}>
      <h2 className={`font-bold text-gray-900 mb-6 ${device === "desktop" ? "text-3xl" : "text-2xl"}`}>{data.title}</h2>
      <div className={`grid gap-4 ${colMap[device]}`}>
        {data.images.map((img, i) => (
          <div key={i} className="aspect-video rounded-2xl overflow-hidden bg-gray-100">
            <img src={img.url} alt={img.caption} className="w-full h-full object-cover object-top" />
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Page Renderer ─────────────────────────────────────────────────────────────
const PageRenderer: React.FC<{ page: BuilderPage; device: Device }> = ({ page, device }) => (
  <div>
    {page.blocks.map((block) => {
      switch (block.data.type) {
        case "hero": return <HeroRender key={block.id} data={block.data as HeroBlockData} device={device} />;
        case "story": return <StoryRender key={block.id} data={block.data as StoryBlockData} device={device} />;
        case "applications": return <ApplicationsRender key={block.id} data={block.data as ApplicationsBlockData} device={device} />;
        case "certifications": return <CertificationsRender key={block.id} data={block.data as CertificationsBlockData} device={device} />;
        case "cta": return <CTARender key={block.id} data={block.data as CTABlockData} device={device} />;
        case "specs_grid": return <SpecsRender key={block.id} data={block.data as SpecsGridBlockData} device={device} />;
        case "gallery": return <GalleryRender key={block.id} data={block.data as GalleryBlockData} device={device} />;
        default: return null;
      }
    })}
  </div>
);

// ── Main Modal ────────────────────────────────────────────────────────────────
const BuilderPreviewModal: React.FC<Props> = ({ pages, productName, onClose }) => {
  const [device, setDevice] = useState<Device>("desktop");
  const [activePageIdx, setActivePageIdx] = useState(0);

  const safePages = pages.filter((p) => p.blocks.length > 0);
  const activePage = safePages[activePageIdx] ?? safePages[0];

  const deviceConfig: Record<Device, { width: string; label: string; icon: string; frameClass: string }> = {
    desktop: { width: "100%", label: "Desktop", icon: "ri-computer-line", frameClass: "w-full" },
    tablet: { width: "768px", label: "Tablet (768px)", icon: "ri-tablet-line", frameClass: "w-[768px]" },
    mobile: { width: "390px", label: "Mobile (390px)", icon: "ri-smartphone-line", frameClass: "w-[390px]" },
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/85 flex flex-col">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-6 py-3 bg-gray-900 border-b border-gray-700 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 flex items-center justify-center bg-[#004A99] rounded-lg">
            <i className="ri-eye-line text-white text-sm" />
          </div>
          <div>
            <p className="text-sm font-bold text-white">Preview — {productName || "Sản phẩm"}</p>
            <p className="text-[10px] text-gray-400">Giao diện người dùng thực sẽ thấy · {deviceConfig[device].label}</p>
          </div>
        </div>

        {/* Device switcher */}
        <div className="flex items-center gap-1 bg-gray-800 rounded-xl p-1">
          {(["desktop", "tablet", "mobile"] as const).map((d) => (
            <button
              key={d}
              onClick={() => setDevice(d)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap transition-colors ${
                device === d ? "bg-[#004A99] text-white" : "text-gray-400 hover:text-white"
              }`}
            >
              <i className={deviceConfig[d].icon} />
              {deviceConfig[d].label.split(" ")[0]}
            </button>
          ))}
        </div>

        <button
          onClick={onClose}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm font-semibold rounded-xl cursor-pointer whitespace-nowrap transition-colors"
        >
          <i className="ri-close-line" />
          Đóng Preview
        </button>
      </div>

      {/* Page tabs (if multiple pages) */}
      {safePages.length > 1 && (
        <div className="flex items-center gap-2 px-6 py-2 bg-gray-800 border-b border-gray-700 overflow-x-auto flex-shrink-0">
          {safePages.map((page, idx) => (
            <button
              key={page.id}
              onClick={() => setActivePageIdx(idx)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap transition-colors ${
                activePageIdx === idx ? "bg-[#004A99] text-white" : "text-gray-400 hover:text-white hover:bg-gray-700"
              }`}
            >
              <i className="ri-file-text-line" />
              {page.name}
            </button>
          ))}
        </div>
      )}

      {/* Preview area */}
      <div className="flex-1 overflow-auto bg-gray-700 flex justify-center p-6">
        {activePage ? (
          <div
            className={`bg-white transition-all duration-300 overflow-y-auto rounded-xl ${deviceConfig[device].frameClass}`}
            style={{ minHeight: "100%", maxWidth: "100%" }}
          >
            {/* Simulated browser bar for tablet/mobile */}
            {device !== "desktop" && (
              <div className={`sticky top-0 z-10 bg-gray-100 border-b border-gray-200 flex items-center gap-2 px-4 py-2`}>
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-yellow-400" />
                  <div className="w-3 h-3 rounded-full bg-green-400" />
                </div>
                <div className="flex-1 bg-white rounded-lg px-3 py-1 text-xs text-gray-400 text-center">
                  {productName || "Sản phẩm"} — Preview
                </div>
              </div>
            )}
            <PageRenderer page={activePage} device={device} />
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center min-h-[60vh] text-gray-400">
            <i className="ri-layout-masonry-line text-6xl mb-4 text-gray-500" />
            <p className="text-lg font-semibold text-gray-300">Chưa có nội dung</p>
            <p className="text-sm mt-1 text-gray-500">Thêm blocks vào Visual Builder để xem preview</p>
          </div>
        )}
      </div>

      {/* Bottom info */}
      <div className="flex items-center justify-between px-6 py-2 bg-gray-900 border-t border-gray-700 flex-shrink-0">
        <p className="text-xs text-gray-500">
          {pages.length} trang · {pages.reduce((acc, p) => acc + p.blocks.length, 0)} blocks
        </p>
        <p className="text-xs text-gray-500">
          <i className="ri-information-line mr-1" />
          Layout tự động điều chỉnh theo từng thiết bị
        </p>
      </div>
    </div>
  );
};

export default BuilderPreviewModal;
